/* eslint-disable @typescript-eslint/no-misused-promises -- Breaks form submission */
import React, {useMemo, useState, useEffect} from 'react'
import type {JSX} from 'react'
import {useForm} from 'react-hook-form'
import type {FieldErrors} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import type * as z from 'zod/v4'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {useRouter} from 'next/router'
import {
	Form,
	CdcEmailField,
	CdcFirstNameField,
	CdcLastNameField,
	CdcRoleField,
	CdcBusinessNameField,
	CdcBusinessSearchField,
	CdcPersonalTelephoneField,
	CdcBusinessTelephoneField,
	CdcProfessionalNumberField,
	CdcSalutationField,
	CdcAddressCityField,
	CdcAddressLine1Field,
	CdcAddressLine2Field,
	CdcAddressStateField,
	CdcAddressZipCodeField,
	CdcCountryField,
	CheckboxGroupField,
	CheckboxField,
	ComboboxField,
	RadioGroupField,
	SelectField,
	Button,
	DisplayText,
	CdcExplicitConsentField,
	CdcExplicitSmsConsentField,
	CdcImplicitConsentField,
	TextField,
	NumberField,
	getCdcBusinessTypeOptions,
	getFieldCopy,
} from '@elanco/forms'
import type {LocaleKey} from '@elanco/forms'
import type {Block, Tersed} from '@/services/kontent-ai/types'
import {useAuth} from '@/products/auth/auth-provider'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'
import {logError, logEvent} from '@/services/logger/client'
import {http} from '@/services/http'
import {env} from '@/services/env/client'
import {pushToDataLayer} from '@/utilities/analytics'
import {toBcp47Locale} from '@/utilities/locales'
import type {Question} from '@/models/campaign/question'
import type {AccountInfoResult} from '@/services/cdc/model'
import {RichTextRenderer} from '@/products/flexible-web-toolkit/components/rich-text-renderer'
import type {CampaignData} from '@/models/campaign/campaign-data'
import {CampaignInterestHandler} from '../../cdc-screen-set-wrapper/campaign-questions/campaign-interest-handler'
import {
	generateFormSchema,
	generateDefaultValues,
	getFormFieldName,
	getTrackingConsentPayload,
	getCampaignTrackingPreferences,
	getExplicitConsentFieldProps,
	getFieldError,
	extractCountryCode,
	getConfiguredRoleOptions,
	getProfessionalNumberRoleValues,
	getJurisdictionMode,
	getProfessionalNumberFieldProps,
	trimFormValues,
	getBusinessProfilePayload,
} from './form-helpers'
import type {
	FormField,
	CheckboxGroupOption,
	RadioGroupOption,
	SelectOption,
} from './model'

// Type for form field content items - represents any form field type
// Using any here because form fields can have different element structures based on field type
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Form fields have varying element structures
type CampaignFormFieldContentItem = IContentItem<Record<string, any>>

export type CampaignFormContentItem = IContentItem<{
	campaignFormCampaignId: Elements.TextElement
	campaignFormQuestions: Elements.LinkedItemsElement<CampaignFormFieldContentItem>
	/** @deprecated Use brandColor instead */
	campaignFormColorStyling: Elements.MultipleChoiceElement
	/** New standardized brand color field from color picker */
	brandColor: Elements.CustomElement
	campaignFormHideOnLogin: Elements.MultipleChoiceElement
	campaignFormRedirectPath: Elements.TextElement
	submissionFailureText: Elements.TextElement
	successScreenMessage: Elements.RichTextElement
	successScreenButtonText: Elements.TextElement
	formDisplayTextAlignment: Elements.MultipleChoiceElement
	formDisplayTextContent: Elements.RichTextElement
}>

/**
 * Map form field types to their corresponding form components
 */
const FIELD_TYPE_MAPPING = {
	form_cdc_email_field: CdcEmailField,
	form_cdc_first_name_field: CdcFirstNameField,
	form_cdc_last_name_field: CdcLastNameField,
	form_cdc_role_field: CdcRoleField,
	form_cdc_business_name_field: CdcBusinessNameField,
	form_cdc_business_search_field: CdcBusinessSearchField,
	form_cdc_telephone_field: CdcPersonalTelephoneField,
	form_cdc_business_telephone_field: CdcBusinessTelephoneField,
	form_cdc_salutation_field: CdcSalutationField,
	form_cdc_address_city_field: CdcAddressCityField,
	form_cdc_address_line1_field: CdcAddressLine1Field,
	form_cdc_address_line2_field: CdcAddressLine2Field,
	form_cdc_address_state_field: CdcAddressStateField,
	form_cdc_address_zip_code_field: CdcAddressZipCodeField,
	form_cdc_country_field: CdcCountryField,
	form_checkbox_group_field: CheckboxGroupField,
	form_checkbox_field: CheckboxField,
	form_combobox_field: ComboboxField,
	form_button: Button,
	form_display_text: DisplayText,
	form_radio_group_field: RadioGroupField,
	form_select_field: SelectField,
	form_cdc_explicit_consent_field: CdcExplicitConsentField,
	form_cdc_explicit_sms_consent_field: CdcExplicitSmsConsentField,
	form_cdc_implicit_consent_field: CdcImplicitConsentField,
	form_text_field: TextField,
	form_number_field: NumberField,
} as const

/**
 * Resolve the email consent value from the two possible consent checkboxes.
 * Returns a boolean when either consent field is present on the form, or
 * `null` when neither exists so email consent is left untouched. Avoids
 * emitting `undefined`, which fails the API's `boolean | null` validation.
 */
const resolveConsent = (
	explicitConsent: unknown,
	implicitConsent: unknown
): boolean | null => {
	const hasExplicitConsent = typeof explicitConsent === 'boolean'
	const hasImplicitConsent = typeof implicitConsent === 'boolean'
	if (!hasExplicitConsent && !hasImplicitConsent) {
		return null
	}
	return Boolean(explicitConsent) || Boolean(implicitConsent)
}

const CampaignFormBlockComponent = ({
	block,
	BlockMapper,
	globalConfig,
	page,
	requireBusinessDetails = true,
}: Parameters<Block<CampaignFormContentItem>>[0] & {
	requireBusinessDetails?: boolean
}): ReturnType<Block<CampaignFormContentItem>> => {
	const {authenticated, ready, userDetails} = useAuth()
	const {locale = '', ...router} = useRouter()

	const [isSubmitted, setIsSubmitted] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [submissionError, setSubmissionError] = useState<string | null>(null)
	const [identityFieldError, setIdentityFieldError] = useState<string | null>(
		null
	)

	const campaignInterestHandler = new CampaignInterestHandler()

	// Color priority system: block level (new > legacy) > fallback
	const {hexCode} = colorWithFallback({
		blockColor: block.elements.brandColor,
		blockColorLegacy: block.elements.campaignFormColorStyling,
		fallbackColorId: 'elanco-blue',
		fallbackHexColor: '#0072CE',
	})
	const LOCALE = toBcp47Locale(locale, 'en-US') as LocaleKey
	const formQuestions: FormField[] = useMemo(
		() =>
			Array.isArray(block.elements.campaignFormQuestions)
				? (block.elements.campaignFormQuestions as FormField[])
				: [],
		[block.elements.campaignFormQuestions]
	)
	const hideFormOnLogin =
		block.elements.campaignFormHideOnLogin[0]?.codename === 'yes' &&
		authenticated

	const redirectPath = block.elements.campaignFormRedirectPath || ''

	// Generate form schema and default values dynamically (with safe fallbacks)
	const hasBusinessSearch = formQuestions.some(
		(field) =>
			field.system.type === 'form_cdc_business_search_field' &&
			Boolean(field.system.codename)
	)
	const schemaResult = generateFormSchema(formQuestions, LOCALE, {
		requireBusinessDetails,
	})
	const defaultValuesResult = generateDefaultValues(formQuestions)

	// Check for errors in form generation
	const formGenerationError = schemaResult.error || defaultValuesResult.error

	const formSchema = schemaResult.data
	const defaultValues = defaultValuesResult.data

	// Initialize form with react-hook-form (always call hooks unconditionally)
	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues,
	})

	// Watch role selection to drive conditional professional number visibility
	const selectedRole = form.watch('role') as string | undefined

	useEffect(() => {
		if (authenticated) {
			if (userDetails.emailAddress) {
				form.setValue('email', userDetails.emailAddress)
				form.setValue('confirmEmail', userDetails.emailAddress)
			}
			if (userDetails.firstName) {
				form.setValue('firstName', userDetails.firstName)
			}
			if (userDetails.lastName) {
				form.setValue('lastName', userDetails.lastName)
			}
		}
	}, [
		authenticated,
		userDetails.emailAddress,
		userDetails.firstName,
		userDetails.lastName,
		form,
	])

	// Clear professional details when not applicable, without retaining hidden jurisdiction.
	useEffect(() => {
		const roleField = formQuestions.find(
			(f) => f.system.type === 'form_cdc_role_field'
		)
		const rolesRequiringProfessionalNumber = roleField
			? getProfessionalNumberRoleValues(roleField)
			: []
		const roleRequiresProfNumber =
			rolesRequiringProfessionalNumber.length > 0 &&
			Boolean(selectedRole) &&
			rolesRequiringProfessionalNumber.includes(selectedRole ?? '')
		if (
			rolesRequiringProfessionalNumber.length > 0 &&
			roleField &&
			!roleRequiresProfNumber
		) {
			form.setValue('professionalNumber', undefined)
		} else if (
			roleField &&
			roleRequiresProfNumber &&
			getJurisdictionMode(roleField) === 'hidden'
		) {
			const professionalNumber = form.getValues('professionalNumber')
			if (
				typeof professionalNumber === 'object' &&
				professionalNumber !== null
			) {
				form.setValue('professionalNumber', {
					...professionalNumber,
					state: '',
				})
			}
		}
	}, [selectedRole, formQuestions, form])

	// Early return if form should be hidden for authenticated users
	if (hideFormOnLogin) {
		return null
	}

	// Handle form generation errors (unsupported field types)
	if (formGenerationError) {
		logError(
			new Error(
				`Campaign form configuration error: ${formGenerationError.message}. Field: ${
					formGenerationError.fieldType
				}. Field codename: ${
					formGenerationError.fieldCodename
				}. Campaign ID: ${
					block.elements.campaignFormCampaignId || 'unknown'
				}`
			)
		)
		return (
			<div className="mx-auto w-full max-w-2xl rounded-lg bg-white p-6 shadow-sm">
				<div
					className="border-yellow-300 bg-yellow-50 text-yellow-800 "
					role="alert"
				>
					<p className="font-medium">
						{getFieldError(LOCALE, 'form', 'configuration')}
					</p>
					<br />
					<p className="text-sm">
						{getFieldError(LOCALE, 'form', 'formComponentLoad')}
					</p>
					<p className="text-sm">
						{getFieldError(LOCALE, 'form', 'code')}:{' '}
						<strong>{formGenerationError.fieldCodename}</strong>
					</p>
					<p className="text-sm">
						{getFieldError(LOCALE, 'form', 'type')}:{' '}
						<strong>{formGenerationError.fieldType}</strong>
					</p>
					<br />
					<p className="text-sm">
						{getFieldError(LOCALE, 'form', 'reportAnIssue')}
					</p>
				</div>
			</div>
		)
	}

	// Handle empty questions case after hooks are initialized
	if (formQuestions.length === 0) {
		logError(
			new Error(
				`Campaign form rendered with no questions. Campaign ID: ${
					block.elements.campaignFormCampaignId || 'unknown'
				}`
			)
		)
		return (
			<div className="mx-auto w-full max-w-2xl rounded-lg bg-white p-6 shadow-sm">
				<p className="text-gray-500">
					{getFieldError(LOCALE, 'form', 'noFormQuestionsAvailable')}
				</p>
			</div>
		)
	}

	// Validate that required CDC fields (Email + either Explicit or Implicit Consent) are present in the form configuration
	const hasEmailField = formQuestions.some(
		(q) => q.system.type === 'form_cdc_email_field'
	)
	const hasExplicitConsentField = formQuestions.some(
		(q) => q.system.type === 'form_cdc_explicit_consent_field'
	)
	const hasImplicitConsentField = formQuestions.some(
		(q) => q.system.type === 'form_cdc_implicit_consent_field'
	)
	const missingEmailField = !hasEmailField
	const missingExplicitConsentField = !hasExplicitConsentField
	const missingImplicitConsentField = !hasImplicitConsentField
	if (
		missingEmailField ||
		(missingExplicitConsentField && missingImplicitConsentField)
	) {
		const missingFields = [
			...(missingEmailField ? ['Email'] : []),
			...(missingExplicitConsentField && missingImplicitConsentField
				? ['Explicit Consent', 'Implicit Consent']
				: []),
		].join(', ')
		logError(
			new Error(
				`Campaign form is missing required CDC fields: ${missingFields}. Campaign ID: ${
					block.elements.campaignFormCampaignId || 'unknown'
				}`
			)
		)
		return (
			<div className="mx-auto w-full max-w-2xl rounded-lg bg-white p-6 shadow-sm">
				<div
					className="border-yellow-300 bg-yellow-50 text-yellow-800 rounded-md border p-4"
					role="alert"
				>
					<p className="font-medium">
						{getFieldError(LOCALE, 'form', 'configuration')}
					</p>
					<br />
					<p className="text-sm">
						{getFieldError(LOCALE, 'form', 'missingRequiredFields')}
					</p>
					<br />
					<p className="text-sm">
						{getFieldError(LOCALE, 'form', 'reportAnIssue')}
					</p>
				</div>
			</div>
		)
	}

	const onSubmit = async (
		values: z.infer<typeof formSchema>
	): Promise<void> => {
		pushToDataLayer({
			event: 'signup_campaign_initiated',
			action: 'Signup Initiated',
			campaign: block.elements.campaignFormCampaignId || 'unknown',
			user_Id: userDetails.id || null,
		})
		setIsSubmitting(true)
		setSubmissionError(null)
		try {
			const trimmedValues = trimFormValues(values)

			const email =
				(trimmedValues.email as string | undefined) ||
				userDetails.emailAddress
			const firstName =
				(trimmedValues.firstName as string | undefined) ||
				userDetails.firstName ||
				''
			const lastName =
				(trimmedValues.lastName as string | undefined) ||
				userDetails.lastName ||
				''
			const role = (trimmedValues.role as string) || ''

			// Consent is granted when either consent checkbox is ticked. When
			// neither consent field is present on the form, pass null so email
			// consent is left untouched (avoids sending `undefined`, which fails
			// the API's boolean|null Zod validation).
			const consent = resolveConsent(
				trimmedValues.explicitConsent,
				trimmedValues.implicitConsent
			)
			// SMS marketing requires explicit opt-in. When the SMS consent field
			// is present on the form, capture its checkbox value (unticked = false).
			// When the field is absent, pass null so SMS consent is left untouched.
			const smsConsent =
				typeof trimmedValues.smsConsent === 'boolean'
					? trimmedValues.smsConsent
					: null
			const salutation = (trimmedValues.salutation as string) || ''
			// Extract telephone field values
			const telephoneField = trimmedValues.telephone as
				| {
						diallingCode?: string
						localNumber?: string
				  }
				| undefined
			const businessTelephoneField = trimmedValues.businessTelephone as
				| {
						diallingCode?: string
						localNumber?: string
				  }
				| undefined
			const telephone = telephoneField
				? `${telephoneField.diallingCode || ''}${
						telephoneField.localNumber || ''
					}`
				: ''
			const businessTelephone = businessTelephoneField
				? `${businessTelephoneField.diallingCode || ''}${
						businessTelephoneField.localNumber || ''
					}`
				: ''

			// Extract professional number field values
			const professionalNumberField = trimmedValues.professionalNumber as
				| {
						number?: string
						state?: string
				  }
				| undefined
			const professionalNumber = professionalNumberField?.number || ''
			const professionalNumberJurisdiction =
				professionalNumberField?.state || ''

			const createUserPayload = {
				...getBusinessProfilePayload(trimmedValues, hasBusinessSearch),
				emailAddress: email,
				firstName,
				lastName,
				role,
				telephone,
				businessTelephone,
				salutation,
				professionalNumber,
				professionalNumberJurisdiction,
				pets: undefined,
				locale,
				profileLocale: LOCALE,
				sourceName: `${window.location.origin}/${locale}${router.asPath}`,
				consent: locale === 'us' ? true : consent,
				smsConsent,
				trackingConsent: getTrackingConsentPayload(
					formQuestions,
					trimmedValues
				),
			}

			const isAuthenticatedSession =
				authenticated && ready && Boolean(userDetails.id)

			const createUserResponse = isAuthenticatedSession
				? await http.post<AccountInfoResult>(
						'/api/user/upsertAndFetchUser',
						{
							...createUserPayload,
							uid: userDetails.id,
							hasFullAccount: userDetails.light === false,
						}
					)
				: await http.post<AccountInfoResult>(
						'/api/user/createAndFetchUser',
						createUserPayload
					)

			if (
				!createUserResponse.data.success ||
				!createUserResponse.data.data
			) {
				throw new Error(
					createUserResponse.data.error ||
						'Failed to create lite user'
				)
			}

			const userData = createUserResponse.data.data

			//Step 2: Create flat preferences and consent objects
			const subscriptions = userData.subscriptions as
				| {
						preference?: {
							b2b?: Record<
								string,
								{email: {isSubscribed: boolean}} | undefined
							>
						}
				  }
				| undefined
			const b2bPrefs = subscriptions?.preference?.b2b
			const flatPreferences = {
				...getCampaignTrackingPreferences(userData.subscriptions),
				productService:
					b2bPrefs?.ProductServiceCommunications?.email
						.isSubscribed || false,
				businessManagement:
					b2bPrefs?.businessManagement?.email.isSubscribed || false,
				professionalEducation:
					b2bPrefs?.professionalEducation?.email.isSubscribed ||
					false,
				productPromotions:
					b2bPrefs?.ProductPromotions?.email.isSubscribed || false,
			}

			const flatConsent = {
				email: userData.preferences?.email?.isConsentGranted ?? false,
				sms: userData.preferences?.sms?.isConsentGranted ?? false,
			}

			// Step 3: Transform form data to campaign questions format
			const formResponse: Question[] = []

			// Process all non-CDC fields as campaign questions
			Object.entries(trimmedValues).forEach(([key, value]) => {
				// Skip CDC fields - these are part of the CDC profile, not campaign questions
				if (
					key === 'email' ||
					key === 'confirmEmail' ||
					key === 'firstName' ||
					key === 'lastName' ||
					key === 'role' ||
					key === 'businessName' ||
					key === 'businessSearch' ||
					key === 'addressCity' ||
					key === 'addressLine1' ||
					key === 'addressLine2' ||
					key === 'addressState' ||
					key === 'addressPostalCode' ||
					key === 'country' ||
					key === 'salutation' ||
					key === 'telephone' ||
					key === 'businessTelephone' ||
					key === 'professionalNumber' ||
					key === 'professionalNumberJurisdiction' ||
					key === 'explicitConsent' ||
					key === 'implicitConsent' ||
					key === 'smsConsent' ||
					key === 'trackingConsent'
				) {
					return
				}

				// Find the corresponding field definition
				const field = formQuestions.find(
					(f) => getFormFieldName(f) === key
				)

				if (field && field.system.type !== 'form_button') {
					// Get the label from the field
					const questionLabel =
						field.elements?.formNumberFieldLabel ||
						field.elements?.formCheckboxGroupFieldLabel ||
						field.elements?.formCheckboxFieldLabel ||
						field.elements?.formComboboxFieldLabel ||
						field.elements?.formRadioGroupFieldLabel ||
						field.elements?.formSelectFieldLabel ||
						field.elements?.formTextFieldLabel ||
						field.system.name ||
						key

					// Convert value to array of strings
					let answerArray: string[] = []
					if (Array.isArray(value)) {
						answerArray = value.map((v) => String(v))
					} else if (typeof value === 'boolean') {
						answerArray = [value ? 'true' : 'false']
					} else if (value !== null && value !== undefined) {
						answerArray = [String(value)]
					}

					if (answerArray.length > 0) {
						formResponse.push({
							questionId: questionLabel.replaceAll(' ', '-'),
							questionLabel,
							answer: answerArray,
						})
					}
				}
			})

			// Step 4: Build campaign data payload
			const campaignData: CampaignData = {
				submissionId: crypto.randomUUID(),
				submissionTimeStamp: new Date().toISOString(),
				formId: block.elements.campaignFormCampaignId || 'unknown',
				sourceLocale: locale,
				sourcePortal: env.NEXT_PUBLIC_TOWER,
				sourcePage: `${window.location.origin}/${locale}${router.asPath}`,
				formCategory: 'campaign',
				formResponse,
				cdcProfile: {
					UID: userData.UID,
					email,
					firstName,
					lastName,
					phone: userData.profile?.phones?.[0]?.number,
					salutation: userData.data?.salutation,
					role: userData.data?.role,
					role2: userData.data?.role2,
					professionalNumberJurisdiction:
						userData.data?.professionalNumberJurisdiction,
					professionalNumber: userData.data?.professionalNumber,
					preferences: flatPreferences,
					consent: flatConsent,
					business: userData.data?.business,
				},
			}

			// Step 5: Submit to campaign interest API
			logEvent({
				name: 'CAMPAIGN_FORM_SUBMISSION_STARTED',
				properties: {
					campaignId: block.elements.campaignFormCampaignId,
					UID: userData.UID,
				},
			})

			const campaignResponse =
				await campaignInterestHandler.send(campaignData)

			if (campaignResponse) {
				pushToDataLayer({
					event: 'signup_campaign_submitted',
					action: 'Signup Completed',
					status: 'Successful ',
					campaign:
						block.elements.campaignFormCampaignId || 'unknown',
					user_id: userData.UID,
				})

				// Step 6: Redirect immediately if there is a redirect path
				if (redirectPath) {
					void router.push(redirectPath)
				} else {
					// Step 7: Handle success when redirect path is not present
					setIsSubmitted(true)
				}
			} else {
				throw new Error('Failed to submit campaign interest data')
			}
		} catch (error) {
			const errorMessage =
				error instanceof Error
					? error.message
					: 'An unknown error occurred'

			logError(
				new Error(
					`Campaign form submission failed: ${errorMessage}. Campaign ID: ${block.elements.campaignFormCampaignId}`
				)
			)

			pushToDataLayer({
				event: 'signup_campaign_submitted',
				action: 'Signup Failed',
				status: 'Unsuccessful',
				campaign: block.elements.campaignFormCampaignId || 'unknown',
				user_id: userDetails.id || null,
			})

			setSubmissionError(block.elements.submissionFailureText)
		} finally {
			setIsSubmitting(false)
		}
	}

	// handleSubmit only calls onSubmit once validation passes, so a schema
	// failure otherwise fails silently with no visible feedback
	const onInvalid = (
		errors: FieldErrors<z.infer<typeof formSchema>>
	): void => {
		logError(
			new Error(
				`Campaign form failed validation on submit. Invalid fields: ${Object.keys(
					errors
				).join(', ')}. Campaign ID: ${
					block.elements.campaignFormCampaignId || 'unknown'
				}`
			)
		)

		setSubmissionError(block.elements.submissionFailureText)
	}

	// Handle button click in success screen - reload page to trigger pre-population again
	const handleReturnToForm = (): void => {
		if (authenticated) {
			window.location.reload()
		} else {
			form.reset()
			setIsSubmitted(false)
		}
	}

	const getButtonPositionClass = (position: string): string => {
		switch (position) {
			case 'center':
				return 'justify-center'
			case 'left':
				return 'justify-start'
			case 'right':
				return 'justify-end'
			default:
				return 'w-full'
		}
	}

	const renderButtonField = (field: FormField): JSX.Element => {
		const buttonText = field.elements?.formButtonText ?? 'Submit'
		const buttonPosition =
			field.elements?.formButtonPosition?.[0]?.codename ?? 'full_width'
		const positionClass = getButtonPositionClass(buttonPosition)

		return (
			<div className={`flex ${positionClass}`} key={field.system.id}>
				<Button
					className="min-w-32"
					disabled={isSubmitting}
					style={hexCode ? {backgroundColor: hexCode} : undefined}
					type="submit"
				>
					{buttonText}
				</Button>
			</div>
		)
	}

	const setCdcRoleFieldProps = (
		field: FormField,
		fieldProps: Record<string, unknown>
	): void => {
		fieldProps.options = getConfiguredRoleOptions(field)

		fieldProps.required =
			field.elements?.formCdcRoleFieldRequired?.[0]?.codename === 'yes'
	}

	const setCdcBusinessSearchFieldProps = (
		field: FormField,
		fieldProps: Record<string, unknown>
	): void => {
		fieldProps.businessTypeParamName = 'businessTypes'
		fieldProps.inputParamName = 'search'
		fieldProps.countryParamName = 'countryCode'
		fieldProps.allowManualEntry =
			field.elements?.formCdcBusinessSearchFieldAllowManualEntry?.[0]
				?.codename === 'yes'
		const countryCode = extractCountryCode(locale)
		fieldProps.country = countryCode
		fieldProps.searchMode =
			countryCode === 'US' || countryCode === 'AU'
				? 'postal-code'
				: 'business-name'
		fieldProps.locale = toBcp47Locale(locale, 'en-US')
		const localeBusinessTypeOptions = getCdcBusinessTypeOptions(LOCALE)
		fieldProps.apiEndpoint =
			field.elements?.formCdcBusinessSearchFieldApiEndpoint ||
			'/api/business/search'
		fieldProps.required =
			requireBusinessDetails ||
			field.elements?.formCdcBusinessSearchFieldRequired?.[0]
				?.codename === 'yes'
		fieldProps.maxResults = Math.max(
			5,
			Math.min(50, globalConfig.elements.businessSearchMaxResults ?? 10)
		)
		fieldProps.searchPlaceholder =
			field.elements?.formCdcBusinessSearchFieldPlaceholderText || ''
		if (
			fieldProps.allowManualEntry &&
			field.elements?.formCdcBusinessSearchFieldBusinessTypeFilter?.length
		) {
			const selectedBusinessTypeIds = new Set(
				field.elements.formCdcBusinessSearchFieldBusinessTypeFilter.map(
					(item) => item.codename
				)
			)
			const filteredBusinessTypeOptions = localeBusinessTypeOptions
				.filter((option) =>
					selectedBusinessTypeIds.has(option.id || '')
				)
				.map((option) => ({
					label: option.label,
					value: option.value,
				}))
			const businessFilterOptionsNames = [
				...selectedBusinessTypeIds,
			].join(',')

			if (filteredBusinessTypeOptions.length > 0) {
				fieldProps.businessTypeOptions = filteredBusinessTypeOptions
			}
			fieldProps.businessTypeFilterOptions = businessFilterOptionsNames
		}
	}

	const setCheckboxGroupFieldProps = (
		field: FormField,
		fieldProps: Record<string, unknown>
	): void => {
		fieldProps.label =
			field.elements?.formCheckboxGroupFieldLabel || 'Select Options'
		fieldProps.description =
			field.elements?.formCheckboxGroupFieldDescription || ''
		fieldProps.layout =
			field.elements?.formCheckboxGroupFieldLayout?.[0]?.codename ||
			'vertical'
		fieldProps.required =
			field.elements?.formCheckboxGroupFieldRequired?.[0]?.codename ===
			'yes'

		const options = Array.isArray(
			field.elements?.formCheckboxGroupFieldOptions
		)
			? field.elements?.formCheckboxGroupFieldOptions
			: []

		fieldProps.items =
			Array.isArray(options) &&
			options.map((option: CheckboxGroupOption) => ({
				id: option.elements.formFieldOptionValue,
				label: option.elements.formFieldOptionLabel,
			}))
	}

	const setRadioGroupFieldProps = (
		field: FormField,
		fieldProps: Record<string, unknown>
	): void => {
		fieldProps.label =
			field.elements?.formRadioGroupFieldLabel || 'Select an Option'
		fieldProps.description =
			field.elements?.formRadioGroupFieldDescription || ''
		fieldProps.layout =
			field.elements?.formRadioGroupFieldLayout?.[0]?.codename ||
			'vertical'
		fieldProps.required =
			field.elements?.formRadioGroupFieldRequired?.[0]?.codename === 'yes'

		const options = field.elements?.formRadioGroupFieldOptions
		fieldProps.options = (options ?? []).map(
			(option: RadioGroupOption) => ({
				value: option.elements.formFieldOptionValue,
				label: option.elements.formFieldOptionLabel,
			})
		)
	}

	const setSelectFieldProps = (
		field: FormField,
		fieldProps: Record<string, unknown>
	): void => {
		fieldProps.label =
			field.elements?.formSelectFieldLabel || 'Select an option'
		fieldProps.description =
			field.elements?.formSelectFieldDescription || ''
		fieldProps.placeholder =
			field.elements?.formSelectFieldPlaceholder || 'Select an option...'
		fieldProps.required =
			field.elements?.formSelectFieldRequired?.[0]?.codename === 'yes'

		const options = field.elements?.formSelectFieldOptions
		fieldProps.options = (options ?? []).map((option: SelectOption) => ({
			value: option.elements.formFieldOptionValue,
			label: option.elements.formFieldOptionLabel,
		}))
	}

	const setComboboxFieldProps = (
		field: FormField,
		fieldProps: Record<string, unknown>
	): void => {
		fieldProps.label = field.elements?.formComboboxFieldLabel || ''
		fieldProps.description =
			field.elements?.formComboboxFieldDescription || ''
		fieldProps.placeholder =
			field.elements?.formComboboxFieldPlaceholder || ''
		fieldProps.searchPlaceholder =
			field.elements?.formComboboxFieldSearchPlaceholder || ''
		fieldProps.emptyText = field.elements?.formComboboxFieldEmptyText || ''
		fieldProps.required =
			field.elements?.formComboboxFieldRequired?.[0]?.codename === 'yes'

		const options = field.elements?.formComboboxFieldOptions
		fieldProps.options = (options ?? []).map((option: SelectOption) => ({
			value: option.elements.formFieldOptionValue,
			label: option.elements.formFieldOptionLabel,
		}))
	}

	const setCdcEmailFieldProps = (
		field: FormField,
		fieldProps: Record<string, unknown>
	): void => {
		fieldProps.isConfirmation =
			field.elements?.formCdcEmailFieldRequireConfirmation?.[0]
				?.codename === 'yes'
		if (fieldProps.isConfirmation) fieldProps.name = 'confirmEmail'
		if (!authenticated || !userDetails.emailAddress) return

		fieldProps.description =
			getFieldError(LOCALE, 'email', 'emailAssociatedWithYourAccount') ||
			'Your email address is associated with your account and cannot be changed.'
		fieldProps.disabled = true
	}

	const setCdcRequiredFieldProps = (
		field: FormField,
		fieldProps: Record<string, unknown>,
		fieldType: string
	): void => {
		const requiredMapping: Record<string, string> = {
			form_cdc_first_name_field: 'formCdcFirstNameFieldRequired',
			form_cdc_last_name_field: 'formCdcLastNameFieldRequired',
			form_cdc_business_name_field: 'formCdcBusinessNameFieldRequired',
			form_cdc_country_field: 'formCdcCountryFieldRequired',
			form_cdc_telephone_field: 'formCdcTelephoneFieldRequired',
			form_cdc_business_telephone_field:
				'formCdcBusinessTelephoneFieldRequired',
			form_cdc_salutation_field: 'formCdcSalutationFieldRequired',
			form_cdc_address_city_field: 'formCdcAddressCityFieldRequired',
			form_cdc_address_line1_field: 'formCdcAddressLine1FieldRequired',
			form_cdc_address_line2_field: 'formCdcAddressLine2FieldRequired',
			form_cdc_address_state_field: 'formCdcAddressStateFieldRequired',
			form_cdc_address_zip_code_field:
				'formCdcAddressZipCodeFieldRequired',
		}

		const requiredField = requiredMapping[fieldType]
		if (requiredField) {
			// Access required field using bracket notation with proper typing
			const elements = (field.elements ?? {}) as Record<string, unknown>
			const requiredValue = elements[requiredField] as
				| {codename?: string}[]
				| undefined
			fieldProps.required = requiredValue?.[0]?.codename === 'yes'
		}
		if (
			requireBusinessDetails &&
			!hasBusinessSearch &&
			(fieldType === 'form_cdc_business_name_field' ||
				fieldType === 'form_cdc_address_city_field')
		) {
			fieldProps.required = true
		}

		switch (fieldType) {
			case 'form_cdc_first_name_field':
				fieldProps.disabled =
					authenticated && Boolean(userDetails.firstName)
				break
			case 'form_cdc_last_name_field':
				fieldProps.disabled =
					authenticated && Boolean(userDetails.lastName)
				break
		}

		// Add show prop for business telephone field
		if (fieldType === 'form_cdc_business_telephone_field') {
			const elements = field.elements as Record<string, unknown>
			const showValue = elements.formCdcBusinessTelephoneFieldShow as
				| {codename?: string}[]
				| undefined
			fieldProps.show = showValue?.[0]?.codename === 'yes'
		}
	}

	/**
	 * Set props for other field types
	 */
	const setOtherFieldProps = (
		field: FormField,
		fieldProps: Record<string, unknown>,
		fieldType: string
	): void => {
		switch (fieldType) {
			case 'form_display_text':
				fieldProps.textAlign =
					field.elements?.formDisplayTextAlignment?.[0]?.codename ||
					'left'
				fieldProps.text = field.elements?.formDisplayTextContent?.value
				break
			case 'form_checkbox_field':
				fieldProps.label = field.elements?.formCheckboxFieldLabel
				fieldProps.description =
					field.elements?.formCheckboxFieldDescription
				fieldProps.required =
					field.elements?.formCheckboxFieldRequired?.[0]?.codename ===
					'yes'
				break
			case 'form_cdc_explicit_consent_field':
				Object.assign(fieldProps, getExplicitConsentFieldProps(field))
				break
			case 'form_cdc_explicit_sms_consent_field':
				fieldProps.label =
					field.elements?.formCdcExplicitSmsConsentCheckboxFieldLabel?.value
				break
			case 'form_cdc_implicit_consent_field':
				fieldProps.label =
					field.elements?.formCdcImplicitConsentCheckboxFieldLabel?.value
				break
			case 'form_text_field':
				fieldProps.label = field.elements?.formTextFieldLabel || ''
				fieldProps.description =
					field.elements?.formTextFieldDescription || ''
				fieldProps.placeholder =
					field.elements?.formTextFieldPlaceholder || ''
				fieldProps.required =
					field.elements?.formTextFieldRequired?.[0]?.codename ===
					'yes'
				break
			case 'form_number_field':
				fieldProps.required =
					field.elements?.formNumberFieldRequired?.[0]?.codename ===
					'yes'
				fieldProps.description =
					field.elements?.formNumberFieldDescription || ''
				fieldProps.label = field.elements?.formNumberFieldLabel || ''
				fieldProps.placeholder =
					field.elements?.formNumberFieldPlaceholder || ''
				break
		}
	}

	/**
	 * Get field props based on field type
	 */
	const getFieldProps = (
		field: FormField,
		fieldName: string,
		fieldType: string
	): Record<string, unknown> => {
		const fieldProps: Record<string, unknown> = {
			control: form.control,
			locale: LOCALE,
			name: fieldName,
		}

		// Use switch statement to reduce complexity
		switch (fieldType) {
			case 'form_cdc_role_field':
				setCdcRoleFieldProps(field, fieldProps)
				break
			case 'form_cdc_business_search_field':
				setCdcBusinessSearchFieldProps(field, fieldProps)
				break
			case 'form_checkbox_group_field':
				setCheckboxGroupFieldProps(field, fieldProps)
				break
			case 'form_radio_group_field':
				setRadioGroupFieldProps(field, fieldProps)
				break
			case 'form_select_field':
				setSelectFieldProps(field, fieldProps)
				break
			case 'form_combobox_field':
				setComboboxFieldProps(field, fieldProps)
				break
			case 'form_cdc_email_field':
				setCdcEmailFieldProps(field, fieldProps)
				break
			case 'form_cdc_first_name_field':
			case 'form_cdc_last_name_field':
			case 'form_cdc_business_name_field':
			case 'form_cdc_telephone_field':
			case 'form_cdc_business_telephone_field':
			case 'form_cdc_salutation_field':
			case 'form_cdc_address_city_field':
			case 'form_cdc_address_line1_field':
			case 'form_cdc_address_line2_field':
			case 'form_cdc_address_state_field':
			case 'form_cdc_address_zip_code_field':
			case 'form_cdc_country_field':
				setCdcRequiredFieldProps(field, fieldProps, fieldType)
				break
			default:
				setOtherFieldProps(field, fieldProps, fieldType)
				break
		}

		return fieldProps
	}

	/**
	 * Render the role field with its conditional professional-number field.
	 */
	const renderRoleField = (
		field: FormField,
		fieldElement: JSX.Element
	): JSX.Element | null => {
		const rolesRequiringProfNum = getProfessionalNumberRoleValues(field)
		if (rolesRequiringProfNum.length === 0) return null

		const roleRequiresProfNumber =
			Boolean(selectedRole) &&
			rolesRequiringProfNum.includes(selectedRole ?? '')
		return (
			<>
				{fieldElement}
				{roleRequiresProfNumber ? (
					<CdcProfessionalNumberField
						{...getProfessionalNumberFieldProps(field)}
						control={form.control}
						locale={LOCALE}
						name="professionalNumber"
					/>
				) : null}
			</>
		)
	}

	const isConsentFieldType = (fieldType: string): boolean =>
		fieldType === 'form_cdc_explicit_consent_field' ||
		fieldType === 'form_cdc_explicit_sms_consent_field' ||
		fieldType === 'form_cdc_implicit_consent_field'

	const getIdentityFieldInfo = (
		fieldType: string,
		fieldName: string
	): {
		copy: {label: string}
		errorMessage: string | undefined
	} | null => {
		if (!authenticated) return null

		let fieldKey: 'email' | 'firstName' | 'lastName' | null = null
		if (fieldType === 'form_cdc_email_field' && userDetails.emailAddress) {
			fieldKey = 'email'
		} else if (
			fieldType === 'form_cdc_first_name_field' &&
			userDetails.firstName
		) {
			fieldKey = 'firstName'
		} else if (
			fieldType === 'form_cdc_last_name_field' &&
			userDetails.lastName
		) {
			fieldKey = 'lastName'
		}
		if (!fieldKey) return null

		const copyFieldKey =
			fieldKey === 'email' && fieldName === 'confirmEmail'
				? 'confirmEmail'
				: fieldKey
		const errorType =
			fieldKey === 'email'
				? 'authenticatedEmailUpdateTrial'
				: 'authenticatedNameUpdateTrial'

		return {
			copy: getFieldCopy(LOCALE, copyFieldKey) as {label: string},
			errorMessage: getFieldError(LOCALE, copyFieldKey, errorType),
		}
	}

	const renderIdentityField = (
		fieldElement: JSX.Element,
		fieldName: string,
		label: string,
		errorMessage: string | undefined
	): JSX.Element => (
		<div>
			<div className="relative">
				<fieldset
					className="m-0 min-w-0 border-0 bg-transparent p-0 opacity-100 disabled:bg-transparent disabled:opacity-100"
					disabled
				>
					{fieldElement}
				</fieldset>
				<button
					aria-describedby={
						identityFieldError === fieldName
							? `identity-field-error-${fieldName}`
							: undefined
					}
					aria-label={`${label}. ${errorMessage}`}
					className="absolute inset-0 z-10 cursor-text bg-transparent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
					onClick={() => {
						setIdentityFieldError(fieldName)
					}}
					type="button"
				/>
			</div>
			{identityFieldError === fieldName && (
				<p
					className="mt-1 text-[13px] font-bold !text-[#ce4635]"
					id={`identity-field-error-${fieldName}`}
					role="alert"
				>
					{errorMessage}
				</p>
			)}
		</div>
	)

	/** Render a form field using its CMS type and current account context. */
	const renderField = (field: FormField): JSX.Element | null => {
		const {type: fieldType} = field.system
		const fieldName = getFormFieldName(field)
		if (!fieldName) return null
		if (fieldType === 'form_button') return renderButtonField(field)

		const FieldComponent =
			FIELD_TYPE_MAPPING[fieldType as keyof typeof FIELD_TYPE_MAPPING]
		const fieldProps = getFieldProps(field, fieldName, fieldType)
		const fieldElement = (
			// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic field props require flexibility TODO remove any
			<FieldComponent {...(fieldProps as any)} />
		)

		if (fieldType === 'form_cdc_role_field') {
			const roleField = renderRoleField(field, fieldElement)
			if (roleField) return roleField
		}

		const identityField = getIdentityFieldInfo(fieldType, fieldName)
		return (
			<div
				className={
					isConsentFieldType(fieldType)
						? '[&_a:active]:text-secondary [&_a:hover]:text-primary-dark [&_a]:text-primary [&_a]:underline'
						: undefined
				}
			>
				{identityField !== null
					? renderIdentityField(
							fieldElement,
							fieldName,
							identityField.copy.label,
							identityField.errorMessage
						)
					: fieldElement}
			</div>
		)
	}

	if (isSubmitted) {
		return (
			<div className="mx-auto my-8 w-full max-w-2xl rounded-lg bg-white p-6 text-center shadow-sm">
				<div className="space-y-6">
					{/* Success message content from CMS */}
					<RichTextRenderer
						BlockMapper={BlockMapper}
						element={block.elements.successScreenMessage}
						globalConfig={globalConfig}
						page={page}
					/>
					<div className="flex justify-center">
						<Button
							className="min-w-32"
							onClick={handleReturnToForm}
							style={
								hexCode ? {backgroundColor: hexCode} : undefined
							}
							type="button"
						>
							{block.elements.successScreenButtonText || 'OK'}
						</Button>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="mx-auto my-8 w-full max-w-2xl rounded-lg bg-white p-6 shadow-sm">
			<Form {...form}>
				<form
					className="cmp-form space-y-3"
					onSubmit={form.handleSubmit(onSubmit, onInvalid)}
				>
					{submissionError !== null && (
						<div
							className="bg-red-50 text-red-800 border-black-400 rounded-md border p-4"
							role="alert"
						>
							<p className="font-arial text-[15px] font-medium">
								Error
							</p>
							<p className="font-arial text-[15px]">
								{submissionError}
							</p>
						</div>
					)}
					{formQuestions.map((field) => (
						<React.Fragment key={field.system.codename}>
							{renderField(field)}
						</React.Fragment>
					))}
				</form>
			</Form>
		</div>
	)
}

export const CampaignFormBlock = CampaignFormBlockComponent

// Admin Campaign Form — same component, different Kontent.ai element codenames
export type AdminCampaignFormContentItem = IContentItem<{
	adminCampaignFormCampaignId: Elements.TextElement
	adminCampaignFormQuestions: Elements.LinkedItemsElement<CampaignFormFieldContentItem>
	/** @deprecated Use adminBrandColor instead */
	adminCampaignFormColorStyling: Elements.MultipleChoiceElement
	/** New standardized brand color field from color picker */
	adminBrandColor: Elements.CustomElement
	adminCampaignFormHideOnLogin: Elements.MultipleChoiceElement
	adminCampaignFormRedirectPath: Elements.TextElement
	adminSubmissionFailureText: Elements.TextElement
	adminSuccessScreenMessage: Elements.RichTextElement
	adminSuccessScreenButtonText: Elements.TextElement
}>

const AdminCampaignFormBlockComponent: Block<AdminCampaignFormContentItem> = ({
	block,
	BlockMapper,
	globalConfig,
	page,
}) => {
	// Remap admin-prefixed element names to match CampaignFormContentItem shape
	const mappedBlock = {
		...block,
		elements: {
			...block.elements,
			campaignFormCampaignId: block.elements.adminCampaignFormCampaignId,
			campaignFormQuestions: block.elements.adminCampaignFormQuestions,
			campaignFormColorStyling:
				block.elements.adminCampaignFormColorStyling,
			brandColor: block.elements.adminBrandColor,
			campaignFormHideOnLogin:
				block.elements.adminCampaignFormHideOnLogin,
			campaignFormRedirectPath:
				block.elements.adminCampaignFormRedirectPath,
			submissionFailureText: block.elements.adminSubmissionFailureText,
			successScreenMessage: block.elements.adminSuccessScreenMessage,
			successScreenButtonText:
				block.elements.adminSuccessScreenButtonText,
		},
	} as unknown as Tersed<CampaignFormContentItem>
	return (
		<CampaignFormBlockComponent
			BlockMapper={BlockMapper}
			block={mappedBlock}
			globalConfig={globalConfig}
			page={page}
			requireBusinessDetails={false}
		/>
	)
}

export const AdminCampaignFormBlock = AdminCampaignFormBlockComponent
